import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SEOHead, SEO_CONFIG } from '../components/SEOHead';
import { 
  Phone, MessageCircle, ArrowRight, Star, Shield, Award, 
  Clock, BadgeCheck, Users, Hammer, Target, Heart
} from 'lucide-react';

const expertise = [
  'Aluminium Partitions',
  'Aluminium Doors',
  'Aluminium Frames',
  'Aluminium Windows',
  'Sliding Systems',
  'Front Elevation Work',
  'Toughened Glass Partitions',
  'Toughened Glass Doors',
  'Glass Railings',
  'Stairs Glass Work',
  'Shower Enclosures',
  'ACP & HPL Cladding',
];

const whyChooseUs = [
  { icon: Shield, title: 'Quality Materials', desc: 'We use only premium grade aluminium and toughened glass from trusted suppliers.' },
  { icon: BadgeCheck, title: 'Reasonable Pricing', desc: 'Competitive prices without compromising on quality or craftsmanship.' },
  { icon: Award, title: 'Best Service', desc: 'Dedicated team committed to delivering excellence in every project.' },
  { icon: Clock, title: 'On-Time Work', desc: 'We respect deadlines and ensure timely project completion.' },
  { icon: Target, title: 'Custom Designs', desc: 'Tailored solutions to match your specific requirements and preferences.' },
  { icon: Heart, title: 'Local Support', desc: 'Being local means faster response and better after-sales service.' },
];

const stats = [
  { value: '500+', label: 'Projects Completed' },
  { value: '10+', label: 'Years Experience' },
  { value: '5.0', label: 'Google Rating' },
  { value: '100%', label: 'Customer Satisfaction' },
];

export const About = () => {
  const whatsappUrl = `https://wa.me/918512853955?text=${encodeURIComponent('Hi, I want to know more about your services.')}`;

  return (
    <div data-testid="about-page">
      <SEOHead {...SEO_CONFIG.about} />
      
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 md:pt-40 md:pb-32 bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1732897080632-97daf40c3c34?w=1920&q=80')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70"></div>
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <p className="text-orange-400 font-semibold mb-4">ABOUT US</p>
            <h1 className="font-['Oswald'] text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Your Trusted Partner for Premium Glass & Aluminium Solutions
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Raj Decorators - Doing all types of Glass and Aluminium Interior and Exterior works 
              with precision, quality, and dedication since over a decade.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-['Oswald'] text-4xl md:text-5xl font-bold text-sky-600">{stat.value}</p>
                <p className="text-slate-600 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white" data-testid="our-story-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sky-600 font-semibold mb-2">OUR STORY</p>
              <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Crafting Excellence in Glass & Aluminium Since Day One
              </h2>
              <div className="section-divider mb-6"></div>
              
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  <strong className="text-slate-900">Raj Aluminum & Glass Hardware</strong> is a trusted name in 
                  Faridabad and Ballabhgarh for premium quality aluminium and glass fabrication work. What started 
                  as a small workshop has grown into a full-service architectural fabrication business serving 
                  residential, commercial, and industrial clients.
                </p>
                <p>
                  Our journey has been built on three pillars: <span className="text-sky-600 font-semibold">quality materials</span>, 
                  <span className="text-orange-600 font-semibold"> skilled craftsmanship</span>, and 
                  <span className="text-slate-900 font-semibold"> customer satisfaction</span>. We take pride in 
                  every project, whether it's a simple window installation or a complete building facade.
                </p>
                <p>
                  Located in Ballabhgarh, we serve customers across Faridabad, NCR, and nearby areas. Our team of 
                  experienced professionals ensures that every project is executed with precision and completed on time.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/contact">
                  <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="about-quote-btn">
                    Get Free Quote
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-slate-300" data-testid="about-whatsapp-btn">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Chat with Us
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1732897080632-97daf40c3c34?w=800&q=80"
                alt="Our team at work"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl hidden md:block">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Star className="w-7 h-7 text-yellow-500 fill-yellow-500" />
                  </div>
                  <div>
                    <p className="font-['Oswald'] text-2xl font-bold text-slate-900">5.0</p>
                    <p className="text-slate-500 text-sm">Google Rating</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise */}
      <section className="py-20 bg-slate-50" data-testid="expertise-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-sky-600 font-semibold mb-2">OUR EXPERTISE</p>
            <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What We Do Best
            </h2>
            <div className="section-divider mx-auto"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {expertise.map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all"
              >
                <div className="w-3 h-3 bg-gradient-to-br from-sky-500 to-orange-500 rounded-full"></div>
                <span className="text-slate-700 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-sky-600 font-semibold mb-2">WHY CHOOSE US</p>
            <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              The Raj Aluminum Difference
            </h2>
            <div className="section-divider mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <Card key={index} className="border-0 shadow-md hover:shadow-xl transition-shadow hover-scale">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-sky-600 rounded-xl flex items-center justify-center text-white mb-4">
                    <item.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-['Oswald'] font-semibold text-xl text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900" data-testid="about-cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Contact us today for a free consultation and quote. We're here to help bring your vision to life.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:08512853955">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8">
                <Phone className="w-5 h-5 mr-2" />
                Call: 085128 53955
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                Get Free Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
